import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';
import { StatePrivacyRights } from '../components/legal/StatePrivacyRights';
export function PrivacyPolicyPage() {
  usePageMeta('Privacy Policy | findonlinecollege.com', 'Learn how findonlinecollege.com collects, uses, and protects your personal information.');
  return <main className="bg-white py-10 sm:py-16" data-id="element-2153" data-mp="2e0yu13ggra6o">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-gray-800" data-id="element-2154" data-mp="2isgvrtqb9esp">
        <Link to="/" className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-navy mb-8 transition-colors" data-id="element-2155" data-mp="2its2o2f1z2o1">
          <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-2156" data-mp="293p9o27xlopp" />
          Back to home
        </Link>

        {/* Page header */}
        <header className="border-b border-gray-200 pb-8 mb-12" data-id="element-2157" data-mp="1v3eh8uqz422f">
          <h1 className="text-4xl sm:text-5xl font-bold text-navy tracking-tight leading-tight" data-id="element-2158" data-mp="1z4zs6ajy669z">
            Privacy Policy
          </h1>
          <p className="text-sm text-gray-500 mt-3" data-id="element-2159" data-mp="29q211xsqwf8h">
            Last updated:{' '}
            {new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
          </p>
        </header>

        {/* Intro */}
        <div className="space-y-5 leading-relaxed text-base" data-id="element-2160" data-mp="2er95bmvoiwae">
          <p data-id="element-2161" data-mp="1r0c4p69c3leg">
            This privacy notice for findonlinecollege.com describes how and why
            we might collect, store, use, and/or share your information when you
            use our services, such as when you:
          </p>
          <ul className="list-disc pl-6 space-y-2" data-id="element-2162" data-mp="20shy8kf8iavr">
            <li data-id="element-2163" data-mp="1vzor5pwamqgi">
              Visit our website, or any website of ours that links to this
              privacy notice
            </li>
            <li data-id="element-2164" data-mp="1892u0tntuqla">
              Engage with us in other related ways, including any sales,
              marketing, or events
            </li>
          </ul>
          <p data-id="element-2165" data-mp="1szyf1x3pz54d">
            <strong className="text-navy" data-id="element-2166" data-mp="1t0hth3u5vbdn">Questions or concerns?</strong>{' '}
            Reading this privacy notice will help you understand your privacy
            rights and choices. If you do not agree with our policies and
            practices, please do not use our services.
          </p>
        </div>

        {/* Summary of Key Points */}
        <Section title="Summary of Key Points" data-id="element-2167" data-mp="26t7fv9dlqiba">
          <SummaryItem label="What personal information do we process?" text="When you visit, use, or navigate our services, we may process personal information depending on how you interact with us, the choices you make, and the features you use." data-id="element-2168" data-mp="1a1m2s2hihf4k" />
          <SummaryItem label="Do we process sensitive personal information?" text="We do not process sensitive personal information." data-id="element-2169" data-mp="1o14whq9ksnxc" />
          <SummaryItem label="Do we receive information from third parties?" text="We may receive technical and advertising data from third parties, including advertising platforms and analytics providers." data-id="element-2170" data-mp="2uj1spqxx86vj" />
          <SummaryItem label="How do we process your information?" text="To provide, improve, and administer our services, communicate with you, ensure security and fraud prevention, comply with law, and with your consent where required." data-id="element-2171" data-mp="it2dau5cktgv" />
          <SummaryItem label="When and with whom do we share personal information?" text="Only in specific circumstances (e.g., business transfers or service providers)." data-id="element-2172" data-mp="51ntwm7j2q9a" />
          <SummaryItem label="How do we keep your information safe?" text="With organizational and technical measures, but no method is 100% secure." data-id="element-2173" data-mp="3r8m1vfv2pnpl" />
          <SummaryItem label="What are your rights?" text="Depending on your state, you may have rights to access, correct, delete, and port your data, and to opt out of its sale and use for targeted advertising (see Sections 9–11)." data-id="element-2174" data-mp="1phsew7ira47k" />
          <SummaryItem label="How do you exercise your rights?" text="Use the form on our Your Privacy Choices page, email us, or enable Global Privacy Control in your browser (see Section 11)." data-id="element-2175" data-mp="1sk5fmkn2g1vh" />
        </Section>

        {/* TOC */}
        <Section title="Table of Contents" data-id="element-2176" data-mp="1nj96nsnuxpj7">
          <ol className="list-decimal pl-6 space-y-2 text-gray-700" data-id="element-2177" data-mp="1nm3vrxhtfyiy">
            {tocItems.map(item => <li key={item.id} data-id="element-2178" data-mp="1ejm7zemiixtu">
                <a href={`#${item.id}`} className="text-navy hover:text-crimson hover:underline transition-colors" data-id="element-2179" data-mp="1eknv4hgszarx">
                  {item.label}
                </a>
              </li>)}
          </ol>
        </Section>

        {/* Section 1 */}
        <NumberedSection id="section-1" number={1} title="What Information Do We Collect?" data-id="element-2180" data-mp="qvbbsorrhmbw">
          <SubHeading data-id="element-2181" data-mp="uxpth5jp2g1l">Personal information you disclose to us</SubHeading>
          <Paragraph data-id="element-2182" data-mp="c84cxhfjwetd">
            We collect personal information that you voluntarily provide to us
            when you express an interest in obtaining information about us or
            our services, when you participate in activities on the services, or
            otherwise when you contact us.
          </Paragraph>
          <Paragraph data-id="element-2183" data-mp="gb0zfj87ov2l">
            <strong className="text-navy" data-id="element-2184" data-mp="v6715u09y7q4">
              Personal Information Provided by You.
            </strong>{' '}
            The personal information we collect may include:
          </Paragraph>
          <BulletList items={['Names', 'Phone numbers', 'Email addresses', 'Mailing addresses', 'Education level and interests', 'Program preferences']} data-id="element-2185" data-mp="13y93jxtwly31" />

          <SubHeading data-id="element-2186" data-mp="1duc3cx1a9kmt">Information automatically collected</SubHeading>
          <Paragraph data-id="element-2187" data-mp="18z54c2iyl448">
            We automatically collect certain information when you visit, use, or
            navigate the services. This information does not reveal your
            specific identity but may include device and usage information, such
            as:
          </Paragraph>
          <BulletList items={['IP address', 'Browser and device characteristics', 'Operating system', 'Language preferences', 'Referring URLs', 'Device name and type', 'Pages viewed and time spent', 'Clickstream data']} data-id="element-2188" data-mp="fk2ipifsdxkr" />
        </NumberedSection>

        {/* Section 2 */}
        <NumberedSection id="section-2" number={2} title="How Do We Process Your Information?" data-id="element-2189" data-mp="y991f5gkyp6k">
          <Paragraph data-id="element-2190" data-mp="302yni5ghf3em">
            We process your personal information for a variety of reasons,
            including:
          </Paragraph>
          <BulletList items={['To facilitate account creation and authentication', 'To deliver and facilitate delivery of services to you', 'To respond to user inquiries and offer support', 'To send administrative information to you', 'To fulfill and manage your requests', 'To enable user-to-user communications', 'To request feedback and contact you about your use of our services', 'To send you marketing and promotional communications', 'To deliver targeted advertising to you', 'To protect our services and investigate potential violations', 'To identify usage trends and determine the effectiveness of campaigns', "To save or protect an individual's vital interest", 'To comply with our legal obligations']} data-id="element-2191" data-mp="352bi5cdlherr" />

          <SubHeading data-id="element-2192" data-mp="1v1gyqzor85i3">Email Communications</SubHeading>
          <Paragraph data-id="element-2193" data-mp="1r1f59dt6aidw">
            When you submit a form on our website, you may consent to receive
            marketing and promotional emails from findonlinecollege.com and its
            educational partners at the email address you provide. Marketing
            emails are sent from{' '}
            <span className="font-medium text-navy" data-id="element-2194" data-mp="3h7ldhwp64oaf">
              contact@findonlinecollege.com
            </span>
            . Every marketing email includes an unsubscribe link. You may opt
            out at any time and your request will be honored within 10 business
            days. Opting out of marketing email does not affect transactional or
            administrative messages related to a specific inquiry you have
            submitted.
          </Paragraph>
        </NumberedSection>

        {/* Section 3 */}
        <NumberedSection id="section-3" number={3} title="SMS / Text Message Communications" data-id="element-2195" data-mp="8x08761hznoj">
          <SubHeading data-id="element-2196" data-mp="11evw56790wx8">How We Collect SMS Consent</SubHeading>
          <Paragraph data-id="element-2197" data-mp="11ggqaupniduk">
            When you submit a form on our website, you may expressly consent to
            receive autodialed and/or pre-recorded marketing text messages
            (SMS/MMS) from findonlinecollege.com and its educational partners at
            the mobile phone number you provide. This consent is obtained at the
            point of form submission and is documented with the date, time, IP
            address, and page URL at which consent was given.
          </Paragraph>

          <SubHeading data-id="element-2198" data-mp="rx65xyh7vh0z">How We Use Your Phone Number for SMS</SubHeading>
          <Paragraph data-id="element-2199" data-mp="nutco9jnpbl3">
            If you have provided consent, we may send you text messages for
            purposes including:
          </Paragraph>
          <BulletList items={['Information about educational programs matching your interests', 'Follow-up communications related to your inquiry', 'Promotional offers and marketing messages from us or our educational partners']} data-id="element-2200" data-mp="wu6ecg1jgvv9" />

          <SubHeading data-id="element-2201" data-mp="1gj5fvjce4qgs">Consent Is Not Required</SubHeading>
          <Paragraph data-id="element-2202" data-mp="1ginbu0jhl7yb">
            Your consent to receive SMS/MMS messages is never a condition of
            using our services or receiving information. You may choose not to
            provide consent and still access our website and resources.
          </Paragraph>

          <SubHeading data-id="element-2203" data-mp="11tr82fogqh3i">Message Frequency and Rates</SubHeading>
          <Paragraph data-id="element-2204" data-mp="xsnujztw0x5g">
            Message frequency varies based on your interactions and preferences.
            Standard message and data rates may apply depending on your carrier
            and plan.
          </Paragraph>

          <SubHeading data-id="element-2205" data-mp="nvsw1gxm6wr6">How to Opt Out</SubHeading>
          <Paragraph data-id="element-2206" data-mp="nwks0sv4iwhz">
            You may revoke your consent and opt out of SMS communications at any
            time by:
          </Paragraph>
          <BulletList items={['Replying STOP to any text message you receive from us', 'Contacting us directly using the information in Section 14']} data-id="element-2207" data-mp="2i7prrm33aos1" />
          <Paragraph data-id="element-2208" data-mp="2lpwwuk2tnl89">
            Opt-out requests will be honored promptly. After opting out, you may
            receive a single confirmation message. You will not receive further
            SMS marketing messages unless you provide new consent.
          </Paragraph>

          <SubHeading data-id="element-2209" data-mp="2zicuf5cyer1s">For Assistance</SubHeading>
          <Paragraph data-id="element-2210" data-mp="2zk7gwo3vir7z">
            Reply HELP to any text message for support information, or contact
            us directly using the information in Section 14.
          </Paragraph>

          <SubHeading data-id="element-2211" data-mp="26txrhh73jz6t">Third-Party SMS Senders</SubHeading>
          <Paragraph data-id="element-2212" data-mp="2brzci7o328kx">
            When you consent to receive communications from our educational
            partners, those partners may contact you directly via SMS under
            their own messaging programs and privacy policies. We encourage you
            to review the privacy policies of any educational institution or
            partner that contacts you.
          </Paragraph>

          <SubHeading data-id="element-2213" data-mp="32i3lq5e3miyi">Record Retention</SubHeading>
          <Paragraph data-id="element-2214" data-mp="32gs7fbn11st5">
            We retain records of SMS consent, including timestamp, IP address,
            form URL, and submitted form data, in accordance with our general
            data retention practices described in Section 6.
          </Paragraph>
        </NumberedSection>

        {/* Section 4 */}
        <NumberedSection id="section-4" number={4} title="When and With Whom Do We Share Your Personal Information?" data-id="element-2215" data-mp="37fn2bby9r7tm">
          <Paragraph data-id="element-2216" data-mp="2eu3k2af91855">
            We may share information in the following situations:
          </Paragraph>
          <ul className="space-y-3 mt-4" data-id="element-2217" data-mp="2tiq7kzf1567g">
            {[{
            label: 'Business Transfers',
            text: 'In connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition.'
          }, {
            label: 'Educational Institutions',
            text: 'We share your information with schools and educational partners that match your interests.'
          }, {
            label: 'Service Providers',
            text: 'With third-party vendors, service providers, contractors, or agents who perform services for us.'
          }, {
            label: 'Advertising Partners',
            text: 'With advertising partners to deliver targeted ads.'
          }, {
            label: 'Legal Requirements',
            text: 'When required by law or to protect rights, safety, and property.'
          }].map(item => <li key={item.label} className="flex gap-3 leading-relaxed text-gray-700" data-id="element-2218" data-mp="lcebllpbpdkj">
                <span className="text-navy mt-2 w-1.5 h-1.5 rounded-full bg-navy flex-shrink-0" data-id="element-2219" data-mp="36013xoo31od9" />
                <span data-id="element-2220" data-mp="3a2nnl2alo2zd">
                  <strong className="text-navy" data-id="element-2221" data-mp="3a3736ccs2fa8">{item.label}:</strong>{' '}
                  {item.text}
                </span>
              </li>)}
          </ul>
        </NumberedSection>

        {/* Section 5 */}
        <NumberedSection id="section-5" number={5} title="Do We Use Cookies and Other Tracking Technologies?" data-id="element-2222" data-mp="3lh9344gc06ko">
          <Paragraph data-id="element-2223" data-mp="3bkwazb0fq0s1">
            Yes, we use cookies and similar tracking technologies to access or
            store information. See our Cookie Notice for more details about how
            we use cookies.
          </Paragraph>
        </NumberedSection>

        {/* Section 6 */}
        <NumberedSection id="section-6" number={6} title="How Long Do We Keep Your Information?" data-id="element-2224" data-mp="34ksrk0o9ivo">
          <Paragraph data-id="element-2225" data-mp="3v7bxo933psl4">
            We keep your information for as long as necessary to fulfill the
            purposes outlined in this privacy notice unless otherwise required
            by law. When we have no ongoing legitimate business need to process
            your personal information, we will delete or anonymize it.
          </Paragraph>
        </NumberedSection>

        {/* Section 7 */}
        <NumberedSection id="section-7" number={7} title="How Do We Keep Your Information Safe?" data-id="element-2226" data-mp="13dkfhk2zkffs">
          <Paragraph data-id="element-2227" data-mp="18a138d69wrsc">
            We have implemented appropriate technical and organizational
            security measures designed to protect the security of any personal
            information we process. However, no electronic transmission over the
            internet or information storage technology can be guaranteed to be
            100% secure.
          </Paragraph>
        </NumberedSection>

        {/* Section 8 */}
        <NumberedSection id="section-8" number={8} title="Do We Collect Information From Minors?" data-id="element-2228" data-mp="1w1ydhzu2rbrc">
          <Paragraph data-id="element-2229" data-mp="3sv9pzdcq87z9">
            We do not knowingly solicit data from or market to children under 18
            years of age. If you become aware that a child has provided us with
            personal information, please contact us. If we learn we have
            collected personal information from a child under 18, we will delete
            that information.
          </Paragraph>
        </NumberedSection>

        {/* Section 9 */}
        <NumberedSection id="section-9" number={9} title="What Are Your Privacy Rights?" data-id="element-2230" data-mp="18zwkhhnb8ein">
          <Paragraph data-id="element-2231" data-mp="1nwdx0c78jz6s">
            Depending on where you live, you may have the following rights:
          </Paragraph>
          <BulletList items={['The right to know whether we process your personal information and to access it', 'The right to request correction of your personal information', 'The right to request deletion of your personal information', 'The right to obtain a portable copy of your personal information', 'The right to opt out of the sale or sharing of personal information and of targeted advertising', 'The right to opt out of marketing communications', 'The right to appeal our decision on a privacy request']} data-id="element-2232" data-mp="1ixaw93yagf4m" />
          <Paragraph data-id="element-2233" data-mp="2bcg3ul0atgis">
            California residents should see Section 10. Residents of Colorado,
            Connecticut, Delaware, Indiana, Iowa, Kentucky, Maryland, Minnesota,
            Montana, Nebraska, Nevada, New Hampshire, New Jersey, Oregon, Rhode
            Island, Tennessee, Texas, Utah, and Virginia should see Section 11,
            which also explains exactly how to submit a request and how to
            appeal.
          </Paragraph>

          <SubHeading data-id="element-2234" data-mp="1snj8edt60hyz">Opt Out of Marketing Email</SubHeading>
          <Paragraph data-id="element-2235" data-mp="1xjzzngvnvcga">
            To opt out of marketing email: Click the unsubscribe link in any
            marketing email you receive from us, or contact us at the address in
            Section 14. Requests are processed within 10 business days.
          </Paragraph>

          <SubHeading data-id="element-2236" data-mp="241uo70o1v01e">Opt Out of Sale, Sharing, and Targeted Advertising</SubHeading>
          <Paragraph data-id="element-2237" data-mp="23zrwmc790yuk">
            Wherever you live, you can opt out by completing the form on our{' '}
            <Link to="/do-not-sell-my-info" className="text-navy underline hover:text-crimson" data-id="element-2238" data-mp="1u3yco7r7a32r">
              Your Privacy Choices
            </Link>{' '}
            page, by emailing us at the address in Section 14 with the subject
            line <strong className="text-navy" data-id="element-2239" data-mp="1l5d0qvg8hmay">"Privacy Opt-Out,"</strong> or by
            enabling Global Privacy Control in your browser. We will process
            your request within 15 business days.
          </Paragraph>
        </NumberedSection>

        {/* Section 10 */}
        <NumberedSection id="section-10" number={10} title="Your California Privacy Rights (CCPA/CPRA)" data-id="element-2240" data-mp="16fxadgkm7ivs">
          <Paragraph data-id="element-2241" data-mp="xdo4j40akdoq">
            This section applies only to California residents. Under the
            California Consumer Privacy Act (CCPA), as amended by the California
            Privacy Rights Act (CPRA), you have specific rights regarding your
            personal information.
          </Paragraph>

          <SubHeading data-id="element-2242" data-mp="3i17llaun3pej">Categories of Personal Information Collected</SubHeading>
          <Paragraph data-id="element-2243" data-mp="3n0sk5k5wdvqq">
            In the preceding 12 months, we have collected the following
            categories of personal information:
          </Paragraph>
          <BulletList items={['Identifiers (e.g., name, email address, phone number, IP address)', 'Personal information categories listed in the California Customer Records statute (e.g., contact information)', 'Internet or other similar network activity (e.g., browsing history, interaction with our website)', 'Education and program-interest information (e.g., degree level, area of study)', 'Inferences drawn from other personal information (e.g., preferences and characteristics)']} data-id="element-2244" data-mp="2pemvedpwhjck" />

          <SubHeading data-id="element-2245" data-mp="1jioeo8i1jya1">How We Use and Share Your Information</SubHeading>
          <Paragraph data-id="element-2246" data-mp="1ohhrrztf1n9o">
            We use this information to provide our services, match you with
            educational programs, and for marketing purposes. We may share this
            information with the following categories of third parties:
          </Paragraph>
          <BulletList items={['Educational partners (to fulfill your requests for information)', 'Advertising partners (to deliver targeted advertisements)', 'Service providers (to operate our website and services)']} data-id="element-2247" data-mp="10sym7s1r3ola" />

          <SubHeading data-id="element-2248" data-mp="1fhcnhyjir4zq">Your CCPA/CPRA Rights</SubHeading>
          <BulletList items={['Right to Know/Access: You can request information about the categories and specific pieces of personal information we have collected about you.', 'Right to Delete: You can request that we delete your personal information, subject to certain exceptions.', 'Right to Correct: You can request that we correct inaccurate personal information we hold about you.', 'Right to Opt-Out: You have the right to opt out of the sale or sharing of your personal information.', 'Right to Limit Use: You have the right to limit the use and disclosure of sensitive personal information (note: we do not generally collect sensitive personal information).', 'Right to Non-Discrimination: We will not discriminate against you for exercising any of your CCPA rights.']} data-id="element-2249" data-mp="1fj5y1ox969d0" />

          <SubHeading data-id="element-2250" data-mp="28knfdt52v8g0">How to Submit a Request</SubHeading>
          <Paragraph data-id="element-2251" data-mp="23m3tttuebyo6">
            To exercise your Right to Know, Delete, or Correct, use the form on
            our Your Privacy Choices page or contact us using the information
            in Section 14. We also honor Global Privacy Control browser signals
            as a request to opt out of sale and sharing. To exercise your Right to
            Opt-Out of the sale or sharing of your personal information, please
            visit our{' '}
            <Link to="/do-not-sell-my-info" className="text-navy underline hover:text-crimson" data-id="element-2252" data-mp="1owiyqabrr8wi">
              Do Not Sell My Info
            </Link>{' '}
            page.
          </Paragraph>
          <Paragraph data-id="element-2253" data-mp="22pibw5o2wr0w">
            We will confirm receipt of your request within 10 business days and
            aim to respond within 45 days. If we require more time, we will
            inform you of the reason and extension period in writing. You may
            also use an authorized agent to submit a request on your behalf,
            provided you supply written permission and verify your own identity
            directly with us.
          </Paragraph>
        </NumberedSection>

        {/* Section 11 */}
        <NumberedSection id="section-11" number={11} title="Your U.S. State Privacy Rights" data-id="element-2254" data-mp="2qfuwag0igg8d">
          <StatePrivacyRights data-id="element-2255" data-mp="eyszi4gsh5z5" />
        </NumberedSection>

        {/* Section 12 */}
        <NumberedSection id="section-12" number={12} title="Controls for Do-Not-Track and Global Privacy Control" data-id="element-2256" data-mp="a0rcj8g8smie">
          <Paragraph data-id="element-2257" data-mp="jrm48sc9ceb8">
            Most web browsers and some mobile operating systems include a
            Do-Not-Track ("DNT") feature or setting you can activate to signal
            your privacy preference. No uniform technology standard for
            recognizing DNT signals has been finalized, so we do not currently
            respond to DNT signals.
          </Paragraph>
          <Paragraph data-id="element-2258" data-mp="10ulj06drbk6">
            We do recognize the Global Privacy Control ("GPC") signal. When your
            browser sends a GPC signal, we treat it as a request to opt out of
            the sale and sharing of your personal information and of targeted
            advertising for that browser, as required by California, Colorado,
            Connecticut, Delaware, Maryland, Minnesota, Montana, Nebraska, New
            Hampshire, New Jersey, Oregon, Texas, and other applicable state
            laws.
          </Paragraph>
        </NumberedSection>

        {/* Section 13 */}
        <NumberedSection id="section-13" number={13} title="Do We Make Updates to This Notice?" data-id="element-2259" data-mp="3r72vzdpbyo6h">
          <Paragraph data-id="element-2260" data-mp="4ya4na65w6o7">
            Yes, we will update this notice as necessary to stay compliant with
            relevant laws. The updated version will be indicated by an updated
            "Last updated" date at the top of this privacy notice.
          </Paragraph>
        </NumberedSection>

        {/* Section 14 */}
        <NumberedSection id="section-14" number={14} title="How Can You Contact Us About This Notice?" data-id="element-2261" data-mp="invenv1zhbk3">
          <Paragraph data-id="element-2262" data-mp="nmf2k1050xf3">
            If you have questions or comments about this notice, please contact
            us at:
          </Paragraph>
          <div className="mt-5 p-6 bg-gray-50 border border-gray-200 rounded-xl" data-id="element-2263" data-mp="12sb2aj426ezp">
            <p className="font-bold text-navy text-lg" data-id="element-2264" data-mp="xrnbnyjqs060">13th Floor Media</p>
            <p className="text-gray-700 mt-2" data-id="element-2265" data-mp="st3ppy55eodn">
              2870 S Federal Hwy., Suite 237, Deerfield Beach, FL 33441
            </p>
            <p className="text-gray-700 mt-2" data-id="element-2266" data-mp="osa45bf6hlnx">
              Email:{' '}
              <a href="mailto:contact@findonlinecollege.com" className="text-navy underline hover:text-crimson font-medium" data-id="element-2267" data-mp="1lhhpaibvxsrb">
                contact@findonlinecollege.com
              </a>
            </p>
          </div>
        </NumberedSection>
      </div>
    </main>;
}
/* ---------- Sub-components ---------- */
function Section({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <section className="mt-14" data-id="element-2268" data-mp="249k0he5rcgu6">
      <h2 className="text-2xl sm:text-3xl font-bold text-navy mb-6 tracking-tight" data-id="element-2269" data-mp="1zhshppsuffel">
        {title}
      </h2>
      <div className="space-y-4" data-id="element-2270" data-mp="3tounrc4jkzkc">{children}</div>
    </section>;
}
function NumberedSection({
  id,
  number,
  title,
  children
}: {
  id: string;
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return <section id={id} className="mt-16 pt-10 border-t border-gray-200 scroll-mt-8" data-id="element-2271" data-mp="3jyt91ay2wti3">
      <div className="flex items-baseline gap-3 mb-6" data-id="element-2272" data-mp="3oxcvrdkhy9bi">
        <span className="text-crimson font-bold text-xl" data-id="element-2273" data-mp="3az66gltm5hng">{number}.</span>
        <h2 className="text-2xl sm:text-3xl font-bold text-navy tracking-tight leading-tight" data-id="element-2274" data-mp="3b0zhedb9eo19">
          {title}
        </h2>
      </div>
      <div className="space-y-5 leading-relaxed text-gray-700 text-base" data-id="element-2275" data-mp="3118je1nhpqwf">
        {children}
      </div>
    </section>;
}
function SubHeading({
  children
}: {
  children: React.ReactNode;
}) {
  return <h3 className="text-lg font-bold text-navy mt-8 mb-3" data-id="element-2276" data-mp="z8u4lbg8t2k8">{children}</h3>;
}
function Paragraph({
  children
}: {
  children: React.ReactNode;
}) {
  return <p className="text-gray-700 leading-relaxed" data-id="element-2277" data-mp="147x0o6e8vyg2">{children}</p>;
}
function BulletList({
  items
}: {
  items: string[];
}) {
  return <ul className="list-disc pl-6 space-y-2 marker:text-crimson" data-id="element-2278" data-mp="2oo4wjmpz29gq">
      {items.map(item => <li key={item} className="text-gray-700 leading-relaxed" data-id="element-2279" data-mp="2jpjsfc7ln5n0">
          {item}
        </li>)}
    </ul>;
}
function SummaryItem({
  label,
  text
}: {
  label: string;
  text: string;
}) {
  return <div className="border-l-2 border-crimson/40 pl-4 py-1" data-id="element-2280" data-mp="37gyyng2hy73j">
      <p className="text-gray-700 leading-relaxed" data-id="element-2281" data-mp="37fe2jm0jag3k">
        <strong className="text-navy block mb-1" data-id="element-2282" data-mp="33fnf9sa7ui3s">{label}</strong>
        {text}
      </p>
    </div>;
}
const tocItems = [{
  id: 'section-1',
  label: 'What information do we collect?'
}, {
  id: 'section-2',
  label: 'How do we process your information?'
}, {
  id: 'section-3',
  label: 'SMS / text message communications'
}, {
  id: 'section-4',
  label: 'When and with whom do we share your personal information?'
}, {
  id: 'section-5',
  label: 'Do we use cookies and other tracking technologies?'
}, {
  id: 'section-6',
  label: 'How long do we keep your information?'
}, {
  id: 'section-7',
  label: 'How do we keep your information safe?'
}, {
  id: 'section-8',
  label: 'Do we collect information from minors?'
}, {
  id: 'section-9',
  label: 'What are your privacy rights?'
}, {
  id: 'section-10',
  label: 'Your California Privacy Rights (CCPA/CPRA)'
}, {
  id: 'section-11',
  label: 'Your U.S. state privacy rights'
}, {
  id: 'section-12',
  label: 'Controls for do-not-track and Global Privacy Control'
}, {
  id: 'section-13',
  label: 'Do we make updates to this notice?'
}, {
  id: 'section-14',
  label: 'How can you contact us about this notice?'
}];